import React, { useState } from "react";
import { format } from "date-fns";
import { api, errMsg } from "@/lib/api";
import { GradeSelect } from "@/pages/AuthPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const ExpiryPicker = ({ value, onChange, testid }) => (
  <div className="flex gap-2">
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="flex-1 h-11 justify-start bg-white font-normal" data-testid={testid}>
          <CalendarDays className="h-4 w-4 mr-2" />{value ? `Until ${format(value, "MMM d, yyyy")}` : "Never expires"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} disabled={(d) => d < new Date(new Date().toDateString())} />
      </PopoverContent>
    </Popover>
    {value && <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => onChange(null)} aria-label="Clear expiry"><X className="h-4 w-4" /></Button>}
  </div>
);

export const GrantDialog = ({ target, onOpenChange, onDone }) => {
  const [exp, setExp] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${target.user_id}/comp`, { expires_at: exp ? format(exp, "yyyy-MM-dd") : null, note });
      toast.success(`Free access granted to ${target.name}`);
      setExp(null);
      setNote("");
      onDone?.(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="grant-free-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Grant free access</DialogTitle>
          <DialogDescription>
            {target?.name} gets full access without paying.{" "}
            {target?.role === "parent" ? "All their linked children are covered too." : target?.role === "teacher" ? "All students in their classes are covered too." : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Expiry</Label><ExpiryPicker value={exp} onChange={setExp} testid="grant-expiry-button" /></div>
          <div className="space-y-2"><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Scholarship, pilot school, staff" className="h-11" data-testid="grant-note-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="grant-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Grant free access</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const GrantGedDialog = ({ target, onOpenChange, onDone }) => {
  const [exp, setExp] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ged/admin/users/${target.user_id}/comp`, { expires_at: exp ? format(exp, "yyyy-MM-dd") : null, note });
      toast.success(`GED Prep is now free for ${target.name}`);
      setExp(null);
      setNote("");
      onDone?.(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="grant-ged-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Grant free GED Prep</DialogTitle>
          <DialogDescription>
            {target?.name} gets the full GED program for free — 4 courses, AI tutor, and <span className="font-semibold">unlimited practice tests</span> on every subject.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Expiry</Label><ExpiryPicker value={exp} onChange={setExp} testid="grant-ged-expiry-button" /></div>
          <div className="space-y-2"><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Scholarship, veterans program, pilot" className="h-11" data-testid="grant-ged-note-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="grant-ged-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Grant free GED Prep</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AddFreeAccountDialog = ({ open, onOpenChange, onDone }) => {
  const blank = { name: "", email: "", password: "", role: "student", grade_band: "", note: "" };
  const [f, setF] = useState(blank);
  const [free, setFree] = useState(true);
  const [exp, setExp] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.role === "student" && !f.grade_band) return toast.error("Choose a grade level");
    setBusy(true);
    try {
      await api.post("/admin/users", { ...f, grade_band: f.role === "student" ? f.grade_band : null, free_access: free, expires_at: exp ? format(exp, "yyyy-MM-dd") : null });
      toast.success(`Account created for ${f.name}. Share the email and password with them.`);
      setF(blank);
      setExp(null);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="add-account-dialog">
        <DialogHeader><DialogTitle className="font-display">Add an account</DialogTitle><DialogDescription>Create a login for someone and optionally give them free access.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11" data-testid="add-account-name" /></div>
            <div className="space-y-2"><Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger className="h-11 capitalize" data-testid="add-account-role"><SelectValue /></SelectTrigger>
                <SelectContent>{["student", "parent", "teacher", "school", "admin"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {f.role === "student" && <div className="space-y-2"><Label>Grade level</Label><GradeSelect value={f.grade_band} onChange={(v) => setF({ ...f, grade_band: v })} testid="add-account-grade" /></div>}
          <div className="space-y-2"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11" data-testid="add-account-email" /></div>
          <div className="space-y-2"><Label>Temporary password</Label><Input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11" placeholder="At least 6 characters" data-testid="add-account-password" /></div>
          {f.role !== "admin" && (
            <div className="border border-border rounded-xl p-4 space-y-3 bg-[#FBFAF4]">
              <div className="flex items-center justify-between gap-3">
                <div><div className="font-medium text-sm">Free access</div><div className="text-xs text-muted-foreground">No subscription needed{f.role === "parent" ? " — covers their children" : f.role === "teacher" ? " — covers their students" : f.role === "school" ? " — covers all its teachers and students" : ""}.</div></div>
                <Switch checked={free} onCheckedChange={setFree} data-testid="add-account-free-switch" />
              </div>
              {free && (
                <>
                  <ExpiryPicker value={exp} onChange={setExp} testid="add-account-expiry" />
                  <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (optional)" className="h-11 bg-white" data-testid="add-account-note" />
                </>
              )}
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="add-account-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create account</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
